import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getSchemeById, getSchemeApplicationsBySchemeId, getSchemeMembers } from '../../api/web';
import moment from 'moment';

const SchemeDetail = (props) => {
    const id = props.match.params.id;
    const [initialLoading, setInitialLoading] = useState(true);
    const [scheme, setScheme] = useState(null);
    const [activeTab, setActiveTab] = useState('details');
    const [applications, setApplications] = useState([]);
    const [members, setMembers] = useState([]);
    const [applicationsLoading, setApplicationsLoading] = useState(false);
    const [membersLoading, setMembersLoading] = useState(false);

    useEffect(() => {
        getSchemeById(id).then((response) => {
            if (!response.error) {
                setScheme(response.payload);
            }
            setInitialLoading(false);
        });
    }, [id]);

    useEffect(() => {
        if (!id || !scheme) return;
        setApplicationsLoading(true);
        getSchemeApplicationsBySchemeId(id).then((res) => {
            setApplications(Array.isArray(res.payload) ? res.payload : []);
            setApplicationsLoading(false);
        }).catch(() => {
            setApplications([]);
            setApplicationsLoading(false);
        });
    }, [id, scheme]);

    useEffect(() => {
        if (!id || !scheme) return;
        setMembersLoading(true);
        getSchemeMembers(id).then((res) => {
            const list = res.payload;
            setMembers(Array.isArray(list) ? list : []);
            setMembersLoading(false);
        }).catch(() => {
            setMembers([]);
            setMembersLoading(false);
        });
    }, [id, scheme]);

    if (initialLoading) {
        return (
            <div className="content-body">
                <div className="container d-flex justify-content-center ht-100p">
                    <div className="d-flex flex-column align-items-center justify-content-center">
                        <div className="spinner-border" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                        <p className="mg-t-10">Loading scheme</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!scheme) {
        return (
            <div className="content-body">
                <div className="container pd-x-0">
                    <div className="mg-b-20">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                                <li className="breadcrumb-item"><Link to="/dashboard">Menu</Link></li>
                                <li className="breadcrumb-item"><Link to="/scheme">Schemes</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Not found</li>
                            </ol>
                        </nav>
                        <p className="tx-danger">Scheme not found.</p>
                        <Link to="/scheme" className="btn btn-sm btn-primary">Back to Schemes</Link>
                    </div>
                </div>
            </div>
        );
    }

    const crops = scheme.crops || [];
    const inputs = scheme.inputs || [];
    const prerequisites = scheme.prerequisites || [];

    // Derive members from approved applications when scheme_members API returns empty (backend may return list not QuerySet)
    const approvedFarmers = applications
        .filter((app) => (app.application_status || '').toLowerCase() === 'approved' && app.farmer)
        .map((app) => app.farmer);
    const seenIds = new Set();
    const membersFromApplications = approvedFarmers.filter((f) => {
        const fid = f && (f.id != null ? f.id : f.username);
        if (fid == null || seenIds.has(fid)) return false;
        seenIds.add(fid);
        return true;
    });
    const membersList = (members && members.length > 0) ? members : membersFromApplications;

    return (
        <div className="content-body">
            <div className="container pd-x-0">
                <div className="d-sm-flex align-items-center justify-content-between mg-b-20 mg-lg-b-25 mg-xl-b-30">
                    <div>
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                                <li className="breadcrumb-item"><Link to="/dashboard">Menu</Link></li>
                                <li className="breadcrumb-item"><Link to="/scheme">Schemes</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">{scheme.scheme_name}</li>
                            </ol>
                        </nav>
                        <h4 className="mg-b-5 tx-spacing--1">{scheme.scheme_name}</h4>
                        <p className="tx-color-03 tx-13 mg-b-0">
                            Date added: {scheme.date_added ? moment(scheme.date_added).format("Do MMM, YYYY") : '—'}
                        </p>
                    </div>
                    <div className="d-none d-md-block">
                        <Link to="/scheme" className="btn btn-sm pd-x-15 btn-secondary btn-uppercase">
                            <i className="wd-10 mg-r-5 fa fa-arrow-left"></i> Back to list
                        </Link>
                    </div>
                </div>

                <div className="row row-xs">
                    <div className="col-md-12 mg-t-10">
                        <div className="nav-wrapper mg-b-20 tx-13">
                            <ul className="nav nav-line nav-fill tx-medium">
                                <li className="nav-item">
                                    <a href="#" className={`nav-link ${activeTab === 'details' ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); setActiveTab('details'); }}>
                                        <i className="ion-md-information-circle"/> Details
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a href="#" className={`nav-link ${activeTab === 'applications' ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); setActiveTab('applications'); }}>
                                        <i className="ion-md-document"/> Applications ({applications.length})
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a href="#" className={`nav-link ${activeTab === 'members' ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); setActiveTab('members'); }}>
                                        <i className="ion-md-people"/> Members ({membersList.length})
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <div className="tab-content">
                        <div className={`tab-pane fade ${activeTab === 'details' ? 'active show' : ''}`} style={activeTab !== 'details' ? { display: 'none' } : undefined}>
                        <div className="card mg-b-20">
                            <div className="card-header">
                                <h6 className="mg-b-0">Scheme details</h6>
                            </div>
                            <div className="card-body">
                                <div className="row row-sm">
                                    <div className="col-md-6">
                                        <p><strong>Scheme name:</strong> {scheme.scheme_name ?? '—'}</p>
                                        <p><strong>Date added:</strong> {scheme.date_added ? moment(scheme.date_added).format("Do MMM, YYYY HH:mm") : '—'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card mg-b-20">
                            <div className="card-header">
                                <h6 className="mg-b-0">Crops ({crops.length})</h6>
                            </div>
                            <div className="card-body">
                                {crops.length === 0 ? (
                                    <p className="tx-color-03 mg-b-0">No crops.</p>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-sm">
                                            <thead>
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Description</th>
                                                    <th>Crop type</th>
                                                    <th>Date created</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {crops.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>{item.name ?? '—'}</td>
                                                        <td>{item.description ?? '—'}</td>
                                                        <td>{item.crop_type ?? 'Not set'}</td>
                                                        <td>{item.created_at ? moment(item.created_at).format("Do MMM, YYYY") : '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="card mg-b-20">
                            <div className="card-header">
                                <h6 className="mg-b-0">Inputs ({inputs.length})</h6>
                            </div>
                            <div className="card-body">
                                {inputs.length === 0 ? (
                                    <p className="tx-color-03 mg-b-0">No inputs.</p>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-sm">
                                            <thead>
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Description</th>
                                                    <th>Type</th>
                                                    <th>Price</th>
                                                    <th>Unit</th>
                                                    <th>Currency</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {inputs.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>{item.name ?? '—'}</td>
                                                        <td>{item.description ?? '—'}</td>
                                                        <td>{item.input_type ?? 'Not set'}</td>
                                                        <td>{item.price != null ? item.price : '—'}</td>
                                                        <td>{item.unit ?? '—'}</td>
                                                        <td>{item.currency ?? '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="card">
                            <div className="card-header">
                                <h6 className="mg-b-0">Prerequisites ({prerequisites.length})</h6>
                            </div>
                            <div className="card-body">
                                {prerequisites.length === 0 ? (
                                    <p className="tx-color-03 mg-b-0">No prerequisites.</p>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-sm">
                                            <thead>
                                                <tr>
                                                    <th>Type</th>
                                                    <th>Description</th>
                                                    <th>Value</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {prerequisites.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>{item.type ?? '—'}</td>
                                                        <td>{item.description ?? '—'}</td>
                                                        <td>{item.value ?? '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                        </div>

                        <div className={`tab-pane fade ${activeTab === 'applications' ? 'active show' : ''}`} style={activeTab !== 'applications' ? { display: 'none' } : undefined}>
                            <div className="card">
                                <div className="card-header">
                                    <h6 className="mg-b-0">Applications to this scheme</h6>
                                </div>
                                <div className="card-body">
                                    {applicationsLoading ? (
                                        <div className="d-flex align-items-center justify-content-center pd-y-20">
                                            <div className="spinner-border spinner-border-sm text-primary" role="status" />
                                            <span className="mg-l-10 tx-13">Loading...</span>
                                        </div>
                                    ) : applications.length === 0 ? (
                                        <p className="tx-color-03 mg-b-0">No applications for this scheme.</p>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-bordered table-sm">
                                                <thead>
                                                    <tr>
                                                        <th>Farmer</th>
                                                        <th>Status</th>
                                                        <th>Date applied</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {applications.map((app) => {
                                                        const farmer = app && app.farmer ? (typeof app.farmer === 'object' ? app.farmer : {}) : {};
                                                        const name = [farmer.first_name, farmer.last_name].filter(Boolean).join(' ').trim() || farmer.username || '—';
                                                        const status = (app.application_status || '').toLowerCase();
                                                        const statusClass = status === 'approved' ? 'bg-success' : status === 'rejected' ? 'bg-danger' : 'bg-warning text-dark';
                                                        return (
                                                            <tr key={app.id || app.pk || `app-${Math.random()}`}>
                                                                <td>{name}</td>
                                                                <td><span className={`badge ${statusClass}`}>{app.application_status || '—'}</span></td>
                                                                <td>{app.date_applied ? moment(app.date_applied).format("Do MMM, YYYY HH:mm") : '—'}</td>
                                                                <td><Link to="/scheme_application" className="nav-link tx-primary">View</Link></td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className={`tab-pane fade ${activeTab === 'members' ? 'active show' : ''}`} style={activeTab !== 'members' ? { display: 'none' } : undefined}>
                            <div className="card">
                                <div className="card-header">
                                    <h6 className="mg-b-0">Scheme members (approved applicants)</h6>
                                </div>
                                <div className="card-body">
                                    {membersLoading ? (
                                        <div className="d-flex align-items-center justify-content-center pd-y-20">
                                            <div className="spinner-border spinner-border-sm text-primary" role="status" />
                                            <span className="mg-l-10 tx-13">Loading...</span>
                                        </div>
                                    ) : membersList.length === 0 ? (
                                        <p className="tx-color-03 mg-b-0">No members for this scheme.</p>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-bordered table-sm">
                                                <thead>
                                                    <tr>
                                                        <th>Username</th>
                                                        <th>Name</th>
                                                        <th>Phone</th>
                                                        <th>Email</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {membersList.map((user) => {
                                                        const u = user && typeof user === 'object' ? user : {};
                                                        const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || '—';
                                                        return (
                                                            <tr key={u.id || u.username || Math.random()}>
                                                                <td>{u.username ?? '—'}</td>
                                                                <td>{name}</td>
                                                                <td>{u.phone_number ?? u.mobile_number ?? '—'}</td>
                                                                <td>{u.email ?? '—'}</td>
                                                                <td>{u.id ? <Link to={`/user/${u.id}`} className="nav-link tx-primary">View user</Link> : '—'}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SchemeDetail;
